import { Component, Input, inject, output, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ServiceTimelineState } from '../../services/service-timeline.state';
import { HorusLoadingComponent } from '@shared/components/domain/horus-loading/horus-loading.component';
import { ServiceTimelineStep, ServiceTimelineItem } from '../../models/service-timeline.model';
import { ServiceTimelineService } from '../../services/service-timeline.service';
import { DialogManagerService } from '@shared/services/dialog-manager.service';
import { TimelineDetailDrawerComponent } from '../timeline-detail-drawer/timeline-detail-drawer.component';

@Component({
  selector: 'app-service-timeline',
  standalone: true,
  imports: [CommonModule, HorusLoadingComponent],
  templateUrl: './service-timeline.component.html',
  styleUrl: './service-timeline.component.scss',
  providers: [ServiceTimelineState]
})
export class ServiceTimelineComponent {
  private readonly state = inject(ServiceTimelineState);
  private readonly dialogManagerService = inject(DialogManagerService);
  private readonly timelineService = inject(ServiceTimelineService);

  readonly viewMore = output<ServiceTimelineStep>();

  private readonly _timelineData = signal<ServiceTimelineItem[] | null>(null);
  private readonly _useExternalData = signal<boolean>(false);

  @Input()
  set timelineData(value: ServiceTimelineItem[] | null) {
    this._timelineData.set(value);
    this._useExternalData.set(!!value && value.length > 0);
  }

  @Input()
  set serviceDetailId(value: string | undefined) {
    if (value && !this._useExternalData()) {
      this.state.requestTimeline(value);
    }
  }

  readonly lastStepDateChange = output<Date | null>();

  readonly steps = computed(() => {
    if (this._useExternalData()) {
      const data = this._timelineData();
      if (!data) {
        this.lastStepDateChange.emit(null);
        return [];
      }
      const result = this.timelineService.parseTimelineSteps(data);
      const lastStep = result.steps[result.steps.length - 1];
      this.lastStepDateChange.emit(lastStep?.date ?? null);
      return result.steps;
    }
    const steps = this.state.steps();
    const lastStep = steps[steps.length - 1];
    this.lastStepDateChange.emit(lastStep?.date ?? null);
    return steps;
  });

  readonly isLoaded = computed(() => {
    if (this._useExternalData()) {
      return this.steps().length > 0;
    }
    return this.state.isLoaded();
  });

  readonly isLoading = computed(() => {
    if (this._useExternalData()) {
      return false;
    }
    return this.state.isLoading();
  });

  readonly isCanceled = computed(() => {
    if (this._useExternalData()) {
      const data = this._timelineData();
      if (!data) return false;
      const result = this.timelineService.parseTimelineSteps(data);
      return result.isCanceled;
    }
    return this.state.isCanceled();
  });

  readonly isError = computed(() => {
    if (this._useExternalData()) {
      return null;
    }
    return this.state.isError();
  });

  readonly retry = () => this.state.retry();

  onViewMore(step: ServiceTimelineStep, event: MouseEvent): void {
    event.stopPropagation();
    this.viewMore.emit(step);

    // Open timeline detail drawer
    this.dialogManagerService.openDrawer(
      TimelineDetailDrawerComponent,
      { step },
      event,
      '271px'
    );
  }
}
